import React, { useState, useEffect, useCallback } from 'react';
import styles from './ForgotPasswordModal.module.css';
import Icon from '../Icon/Icon';
import apiService from '../../services/apiService';
import { validateEmail } from '../../utils/validators';

/**
 * Modal para solicitar recuperación de contraseña
 * @param {boolean} isOpen - Si el modal está abierto
 * @param {function} onClose - Callback para cerrar el modal
 * @param {function} onBackToLogin - Callback para volver al login
 */
const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin }) => {
  // Estados del formulario
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [emailSent, setEmailSent] = useState(false);

  // Limpiar formulario al cerrar
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setMessage({ type: '', text: '' });
      setEmailSent(false);
      setIsLoading(false);
    }
  }, [isOpen]);

  // Cerrar con Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, isLoading, onClose]);

  // Manejar envío del formulario
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Limpiar mensajes previos
    setMessage({ type: '', text: '' });

    // Validar email
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail) {
      setMessage({ type: 'error', text: 'Ingresa tu correo electrónico' });
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setMessage({ type: 'error', text: 'Ingresa un correo electrónico válido' });
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiService.forgotPassword(trimmedEmail);

      if (result.success) {
        setEmailSent(true);
        setMessage({ 
          type: 'success', 
          text: 'Si el correo existe, recibirás un enlace de recuperación en unos minutos.' 
        });
      } else {
        // Mostrar mensaje genérico por seguridad (no revelar si el email existe)
        if (result.code === 'CONNECTION_ERROR') {
          setMessage({ type: 'error', text: 'Error de conexión. Inténtalo de nuevo.' });
        } else {
          // Por seguridad, mostrar mismo mensaje aunque el email no exista
          setEmailSent(true);
          setMessage({ 
            type: 'success', 
            text: 'Si el correo existe, recibirás un enlace de recuperación en unos minutos.' 
          });
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error inesperado. Inténtalo de nuevo.' });
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  // Manejar click en overlay
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  // Volver al login
  const handleBackToLogin = useCallback(() => {
    if (!isLoading) {
      onClose();
      if (onBackToLogin) {
        onBackToLogin();
      }
    }
  }, [isLoading, onClose, onBackToLogin]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Botón cerrar */}
        <button 
          className={styles.closeButton} 
          onClick={onClose}
          disabled={isLoading}
          aria-label="Cerrar"
        >
          <Icon name="close" size={24} />
        </button>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.iconContainer}>
            <Icon name="key" size={48} />
          </div>
          <h2 className={styles.title}>Recuperar Contraseña</h2>
          <p className={styles.subtitle}>
            {emailSent 
              ? 'Revisa tu bandeja de entrada'
              : 'Ingresa tu correo para recibir un enlace de recuperación'
            }
          </p>
        </div>

        {/* Contenido */}
        <div className={styles.content}>
          {!emailSent ? (
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Campo Email */}
              <div className={styles.inputGroup}>
                <label htmlFor="forgot-email" className={styles.label}>
                  Correo Electrónico
                </label>
                <div className={styles.inputWrapper}>
                  <Icon name="email" size={20} className={styles.inputIcon} />
                  <input
                    type="email"
                    id="forgot-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className={styles.input}
                    disabled={isLoading}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              {/* Mensaje de error/éxito */}
              {message.text && (
                <div className={`${styles.message} ${styles[message.type]}`}>
                  <Icon 
                    name={message.type === 'error' ? 'error' : 'check'} 
                    size={18} 
                  />
                  <span>{message.text}</span>
                </div>
              )}

              {/* Botón enviar */}
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className={styles.spinner}></span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Icon name="send" size={20} />
                    Enviar Enlace
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Vista de confirmación */
            <div className={styles.successView}>
              <div className={styles.successIcon}>
                <Icon name="email" size={64} />
              </div>
              
              {message.text && (
                <div className={`${styles.message} ${styles[message.type]}`}>
                  <Icon 
                    name={message.type === 'error' ? 'error' : 'check'} 
                    size={18} 
                  />
                  <span>{message.text}</span>
                </div>
              )}

              <p className={styles.instructions}>
                El enlace expirará en <strong>1 hora</strong>. Si no ves el correo, 
                revisa tu carpeta de spam.
              </p>

              <button
                type="button"
                className={styles.resendButton}
                onClick={() => {
                  setEmailSent(false);
                  setMessage({ type: '', text: '' });
                }}
              >
                <Icon name="refresh" size={18} />
                Enviar otro enlace
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button 
            type="button" 
            className={styles.backButton}
            onClick={handleBackToLogin}
            disabled={isLoading}
          >
            <Icon name="arrow-left" size={18} />
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
